import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import MultiWindowProjection from './MultiWindowProjection';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch, Target, Crown, ArrowRight, ArrowsClockwise, Lightning, ChartBar } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { ExportButton } from '../components/ExportCSV';
import * as XLSX from 'xlsx';
import { DownloadSimple } from '@phosphor-icons/react';
import useConsensusGroups from '../hooks/useConsensusGroups';
import { GROUP_STYLE, GroupLegend, GroupLetter, ConsensusRefLink, QualityBadge, applyConsensusFilter } from '../components/ConsensusBadge';
import SummaryTabPerWindow from '../components/summary/SummaryTabPerWindow';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMPORTANCE_DATA = [
  { rank: 1, window: 28, name: 'Combination Validator', tier: 'Critical', rationale: 'Filters unrealistic combinations; without this, individual predictions are meaningless', path: '/window28' },
  { rank: 2, window: 18, name: 'Rolling Window Analysis', tier: 'Very High', rationale: 'Recent trends outweigh historical averages in lottery patterns', path: '/window18' },
  { rank: 3, window: 21, name: 'Recency-Weighted Frequency', tier: 'Very High', rationale: 'Corrects the flaw of treating old data equally with new data', path: '/window21' },
  { rank: 4, window: 19, name: 'Markov Chain Analysis', tier: 'Very High', rationale: 'Markov chains, K-Means clustering & anomaly detection — multi-model consensus', path: '/window19' },
  { rank: 5, window: 11, name: 'Positional Hot/Cold', tier: 'High', rationale: 'Positional bias is one of the strongest lottery signals', path: '/window11' },
  { rank: 6, window: 13, name: 'Gap Analysis', tier: 'High', rationale: '"Overdue" numbers have statistically higher reversion probability', path: '/window13' },
  { rank: 7, window: 30, name: 'Jackpot Cycle Analysis (W7 › Cycle Phase)', tier: 'High', rationale: 'Repeat interval tracking; OVERDUE/DUE phase detection — merged into Window 7', path: '/window7?view=cycles' },
  { rank: 8, window: 3, name: 'Per-Ball Difference', tier: 'High', rationale: 'Core mean-reversion + quadratic absence scoring per position', path: '/window3' },
  { rank: 9, window: 14, name: 'Sum Range Analysis', tier: 'High', rationale: 'Strong constraint — eliminates many impossible combinations', path: '/window14' },
  { rank: 10, window: 22, name: 'Draw Similarity / Pattern Matching', tier: 'High', rationale: 'Contextual prediction from historically similar draw patterns', path: '/window22' },
  { rank: 11, window: 2, name: 'Difference Matrix', tier: 'Medium-High', rationale: 'Multiple perspectives on inter-draw number relationships', path: '/window2' },
  { rank: 12, window: 15, name: 'Odd/Even & High/Low', tier: 'Medium-High', rationale: 'Good distribution constraint but not primary predictor', path: '/window15' },
  { rank: 13, window: 6, name: 'Monthly & Annual Trends', tier: 'Medium', rationale: 'Seasonal & long-term frequency patterns', path: '/window6' },
  { rank: 14, window: 16, name: 'Number Pair Correlation', tier: 'Medium', rationale: 'Detects numbers that frequently co-occur', path: '/window16' },
  { rank: 15, window: 20, name: 'Spread & Distribution', tier: 'Medium', rationale: 'Helps with combination realism & spacing', path: '/window20' },
  { rank: 16, window: 8, name: 'Before & After Analysis', tier: 'Medium', rationale: 'Neighbour-draw relationship analysis', path: '/window8' },
  { rank: 17, window: 5, name: '7-Combo Pattern', tier: 'Medium', rationale: 'Extended pattern matching with 7-number sequences', path: '/window3-2' },
  { rank: 18, window: 24, name: 'Ball Position (1-1-1-1-1)', tier: 'Medium', rationale: 'Range distribution and cluster detection', path: '/window24' },
  { rank: 19, window: 17, name: 'Comparison to Previous Draws', tier: 'Medium', rationale: 'Consecutive-draw replication + Day-of-month + Same-Date structural patterns', path: '/window17' },
  { rank: 21, window: 9, name: 'Lucky Star Differences', tier: 'Low-Medium', rationale: 'Core scoring for Lucky Star prediction', path: '/window9' },
  { rank: 22, window: 10, name: 'Lucky Star Appearance & Pairs', tier: 'Low-Medium', rationale: 'Frequency & pair analysis specific to Lucky Stars', path: '/window10' },
  { rank: 23, window: 32, name: 'Prime Number Analysis', tier: 'Low-Medium', rationale: '15 primes in 1-50 — prime bias detection', path: '/window32' },
  { rank: 26, window: 12, name: 'Consecutive Numbers', tier: 'Low', rationale: 'Occurs infrequently in lottery draws', path: '/window12' },
  { rank: 27, window: 25, name: 'Mirror & Complement Numbers', tier: 'Low', rationale: 'Weak signal, mirror-pair frequency', path: '/window25' },
  { rank: 28, window: 26, name: 'Digit Pattern Analysis', tier: 'Low', rationale: 'Last-digit frequency — merged into W51 (Distribution tab)', path: '/digit-affinity?tab=distribution' },
  { rank: 29, window: 23, name: 'Entropy Score', tier: 'Low', rationale: 'Confidence calibration — not a direct predictor', path: '/window23' },
  { rank: 36, window: 28, name: 'Combination Validator', tier: 'Critical', rationale: 'Validates tickets against all criteria', path: '/window28' },
  { rank: 37, window: 1, name: 'Raw Data Dashboard', tier: 'Reference', rationale: 'Data display only — no predictive value', path: '/' },
];

const TIER_COLORS = {
  Critical: 'bg-red-600 text-white',
  'Very High': 'bg-orange-500 text-white',
  High: 'bg-amber-500 text-white',
  'Medium-High': 'bg-yellow-500 text-gray-900',
  Medium: 'bg-blue-500 text-white',
  'Low-Medium': 'bg-gray-500 text-white',
  Low: 'bg-gray-400 text-white',
  Reference: 'bg-gray-300 text-gray-700',
};

const TierBadge = ({ tier }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${TIER_COLORS[tier] || 'bg-gray-200'}`}>{tier}</span>
);

export default function SummaryDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [pwData, setPwData] = useState(null);
  const [pwLoading, setPwLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("combined");
  const [combinedData, setCombinedData] = useState(null);
  const [combinedLoading, setCombinedLoading] = useState(false);
  const [dateSignal, setDateSignal] = useState(null);
  const [digitVerdict, setDigitVerdict] = useState(null);
  const [consensusFilter, setConsensusFilter] = useState("MAX_1_RED"); // default: MAX_1_RED
  const consensus = useConsensusGroups();

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/analysis/summary-dashboard`); setData(res.data); }
      catch { toast.error('Failed to fetch summary dashboard'); }
      finally { setLoading(false); }
    })();
    // Load combined predictions
    (async () => {
      setCombinedLoading(true);
      try { const res = await axios.get(`${API}/analysis/combined-predictions`); setCombinedData(res.data); }
      catch { console.error('Failed to fetch combined predictions'); }
      finally { setCombinedLoading(false); }
    })();
    // Load date pattern signal
    axios.get(`${API}/analysis/date-pattern-signal`).then(r => setDateSignal(r.data)).catch(() => {});
    // Load W17 Digit Verdict — surfaces the top-3 digit-group projection at
    // the top of the dashboard so users see the digit anchor for the next
    // draw before diving into any tab.
    axios.get(`${API}/analysis/date-digit-analysis`).then(r => {
      const proj = r.data?.dg_projection || [];
      setDigitVerdict({
        top3: proj.slice(0, 3),
        all: proj,
        next_draw_date: r.data?.next_draw_date,
      });
    }).catch(() => {});
  }, []);

  const [w3Projection, setW3Projection] = useState(null);
  const loadPerWindowPredictions = async () => {
    if (!w3Projection) {
      // Fetch Window 3 pred_avg — treated as a virtual "window projection"
      try {
        const res2 = await axios.get(`${API}/multi-window/ensemble`);
        setW3Projection(res2.data?.w3_predictions || null);
      } catch { /* noop */ }
    }
    if (pwData) return;
    setPwLoading(true);
    try { const res = await axios.get(`${API}/analysis/per-window-ball-predictions`); setPwData(res.data); }
    catch { toast.error('Failed to fetch per-window predictions'); }
    finally { setPwLoading(false); }
  };

  // Auto-load whenever the per-window tab becomes active (handles all entry paths:
  // initial mount, tab click, page refresh on this tab, programmatic switch).
  useEffect(() => {
    if (activeTab === 'per-window' && !pwData && !pwLoading) {
      loadPerWindowPredictions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const [refreshing, setRefreshing] = useState(false);
  const refreshData = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/analysis/summary-dashboard`);
      setData(res.data);
      setPwData(null);
      toast.success('Data refreshed');
    } catch { toast.error('Failed to refresh'); }
    finally { setRefreshing(false); }
  };

  const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };
  const lsLabels = { lucky_star_1: 'Star 1', lucky_star_2: 'Star 2' };

  const exportPwToExcel = () => {
    if (!pwData) return;
    const wb = XLSX.utils.book_new();
    const rows = [];

    // Header row — now includes Window Number and Remarks columns (user request).
    // Column order: S.NO. | Window Number | Remarks | Window | 1 | 2 | 3 | 4 | 5
    rows.push(['S.NO.', 'Window Number', 'Remarks', 'Window', '1', '2', '3', '4', '5']);

    const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
    const lsKeys = ['lucky_star_1', 'lucky_star_2'];

    // Format the `window` field of a row into a clean "W11" / "W11★" / "W3M" label.
    // pwData uses both numeric (11) and string ("11x21", "11★", "3-MDM") forms.
    const formatWindowNumber = (winValue) => {
      if (winValue == null) return '';
      const s = String(winValue).trim();
      if (s === '') return '';
      // Already prefixed (e.g., "W11x21 BALANCED") — keep as-is with W prefix.
      if (s.toUpperCase().startsWith('W')) return s;
      // For numeric / mixed values, prepend "W".
      return `W${s}`;
    };

    // Main balls
    ballKeys.forEach((bk, bIdx) => {
      rows.push(['', '', '', '', `Ball ${bIdx + 1}`, '', '', '', '']);
      const windows = pwData.balls?.[bk] || [];
      windows.forEach((w, wi) => {
        const winNum = formatWindowNumber(w.window);
        const remark = w.same_as_previous === true ? 'Same as previous projection' : '';
        const row = [wi + 1, winNum, remark, `${w.name}`];
        w.predictions.forEach(p => row.push(p.number));
        while (row.length < 9) row.push('');
        rows.push(row);
      });
      // Average row
      const avgRow = ['', '', '', 'Average'];
      [0, 1, 2, 3, 4].forEach(col => {
        const nums = windows.map(w => w.predictions[col]?.number).filter(n => n != null);
        avgRow.push(nums.length > 0 ? +(nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(1) : '');
      });
      rows.push(avgRow);
    });

    // Lucky Stars
    lsKeys.forEach((lk, lIdx) => {
      rows.push(['', '', '', '', `Ball ${6 + lIdx}`, '', '', '', '']);
      const windows = pwData.lucky_stars?.[lk] || [];
      windows.forEach((w, wi) => {
        const winNum = formatWindowNumber(w.window);
        const remark = w.same_as_previous === true ? 'Same as previous projection' : '';
        const row = [wi + 1, winNum, remark, `${w.name}`];
        w.predictions.forEach(p => row.push(p.number));
        while (row.length < 9) row.push('');
        rows.push(row);
      });
      // Average row
      const avgRow = ['', '', '', 'Average'];
      [0, 1, 2, 3, 4].forEach(col => {
        const nums = windows.map(w => w.predictions[col]?.number).filter(n => n != null);
        avgRow.push(nums.length > 0 ? +(nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(1) : '');
      });
      rows.push(avgRow);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [
      { wch: 6 },   // S.NO.
      { wch: 14 },  // Window Number
      { wch: 30 },  // Remarks
      { wch: 30 },  // Window
      { wch: 6 }, { wch: 6 }, { wch: 6 }, { wch: 6 }, { wch: 6 }, // 1..5
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Window Predictions');
    XLSX.writeFile(wb, `window_predictions_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded flex items-center justify-center"><Crown size={24} weight="fill" className="text-white" /></div>
              <div><h1 className="text-lg font-bold text-gray-900">W41: Summary Dashboard</h1><p className="text-xs text-gray-500">Weighted Consensus Prediction Engine</p></div>
            </div>
            {data && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={refreshData} disabled={refreshing} data-testid="refresh-data-btn" className="gap-1.5">
                  <ArrowsClockwise size={16} className={refreshing ? 'animate-spin' : ''} />
                  {refreshing ? 'Refreshing...' : 'Refresh Data'}
                </Button>
                <ExportButton
                data={data.validated_combinations?.map((c, i) => ({
                  rank: i + 1,
                  ball_1: c.balls[0], ball_2: c.balls[1], ball_3: c.balls[2], ball_4: c.balls[3], ball_5: c.balls[4],
                  lucky_star_1: c.lucky_stars?.[0] || '', lucky_star_2: c.lucky_stars?.[1] || '',
                  sum: c.sum, spread: c.spread, odds: `${c.odds}O/${5 - c.odds}E (${c.odd_even_pct}%)`, high_low: `${c.high || 0}H/${5 - (c.high || 0)}L (${c.high_low_pct || 0}%)`, range: c.range, score: c.consensus_score,
                  w20_spread: c.w20_spread_tier || 'none', w20_quartile: c.w20_quartile_match ? 'Yes' : 'No', w20_overdue: c.w20_overdue_bonus || 0, w20_bonus: c.w20_bonus || 0,
                }))}
                filename="euromillions_consensus_predictions"
                label="Export Top Picks"
              />
              </div>
            )}
          </div>
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">

        <WindowNarrative windowId="W41" {...NARRATIVES.W41} className="mb-5" />
        {loading ? <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div> : data ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="bg-white border border-gray-200 p-1">
              <TabsTrigger value="combined" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white font-bold" data-testid="tab-combined">Combined Predictions</TabsTrigger>
              <TabsTrigger value="multi-window" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white font-bold" data-testid="tab-multi-window">Multi-Window Projection</TabsTrigger>
              <TabsTrigger value="predictions" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-predictions">Consensus Predictions</TabsTrigger>
              <TabsTrigger value="per-window" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-per-window" onClick={loadPerWindowPredictions}>Window Predictions</TabsTrigger>
              <TabsTrigger value="consensus" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-consensus">Per-Ball Scores</TabsTrigger>
              <TabsTrigger value="ranking" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-ranking">Window Importance</TabsTrigger>
            </TabsList>

            {/* W17 Digit Verdict — top-3 digit-group projection for the
                next draw. Sourced from `/analysis/date-digit-analysis` so it
                stays in perfect sync with W17 → Digit Groups tab. Rendered
                above the Missing Digit Rebound Alert so digit context is
                the very first thing the user sees on W41. */}
            {digitVerdict && digitVerdict.top3 && digitVerdict.top3.length > 0 && (
              <div
                className="rounded-xl overflow-hidden border-2 border-emerald-300 shadow-md"
                data-testid="w41-digit-verdict"
              >
                <div className="bg-gradient-to-r from-emerald-700 to-teal-700 text-white px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h3 className="text-sm font-black uppercase tracking-wider">
                      W17 Digit Verdict — Next Draw Projection
                    </h3>
                    <p className="text-[10px] text-emerald-100">
                      Top-3 digit-groups the next draw&apos;s balls are most likely to end in{digitVerdict.next_draw_date ? ` (${digitVerdict.next_draw_date})` : ''}. Sorted by projection score.
                    </p>
                  </div>
                  <a
                    href="/window17?tab=digit-groups"
                    className="text-[11px] font-bold bg-white/15 hover:bg-white/25 transition-colors px-3 py-1 rounded-lg"
                    data-testid="w41-digit-verdict-open"
                  >
                    Open W17 →
                  </a>
                </div>
                <div className="bg-emerald-50/50 px-4 py-3 flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-black uppercase tracking-wider text-emerald-800 mr-2">Top-3 →</span>
                  {digitVerdict.top3.map((p, i) => (
                    <span
                      key={p.digit}
                      className="inline-flex items-center gap-2 bg-white rounded-full px-3 py-1.5 border-2 border-emerald-400 shadow-sm"
                      data-testid={`w41-digit-verdict-slot-${i}`}
                    >
                      <span className="inline-flex w-7 h-7 rounded-full items-center justify-center text-white font-mono font-black text-sm bg-emerald-600">{p.digit}</span>
                      <span className="text-xs">
                        <span className="font-black text-emerald-700">{p.chance_pct}%</span>
                        <span className="text-[9px] text-slate-500 block leading-none">#{i + 1}</span>
                      </span>
                    </span>
                  ))}
                  <span className="text-[10px] text-slate-500 ml-3">
                    Numbers whose last digit ∈ {'{' + digitVerdict.top3.map(p => p.digit).join(', ') + '}'} carry the strongest projection signal
                  </span>
                </div>
              </div>
            )}

            {/* Missing Digit Rebound Alert — Split: Main Balls vs All 7 */}
            {((data.missing_digits_main && Object.keys(data.missing_digits_main).length > 0) || (data.missing_digits_all && Object.keys(data.missing_digits_all).length > 0)) && (
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg px-4 py-3 text-white space-y-2" data-testid="missing-digit-alert">
                {/* Main Balls (B1-B5) row */}
                {data.missing_digits_main && Object.keys(data.missing_digits_main).length > 0 && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs font-bold uppercase tracking-wider text-white/60 w-[140px] shrink-0">Main Balls (B1-B5)</span>
                    <div className="flex items-center gap-2">
                      {Object.values(data.missing_digits_main).sort((a, b) => a.digit - b.digit).map(d => (
                        <span key={d.digit} className={`inline-flex items-center gap-1 backdrop-blur rounded-lg px-2.5 py-1 border ${d.also_missing_all7 ? 'bg-red-500/30 border-red-300/40' : 'bg-white/15 border-white/20'}`}>
                          <span className="font-mono font-bold text-lg">{d.digit}</span>
                          <span className="text-[9px] text-white/70">{d.rebound_pct}%</span>
                          {d.also_missing_all7 && <span className="text-[8px] text-red-200 font-bold">ALL</span>}
                        </span>
                      ))}
                    </div>
                    <span className="text-[10px] text-white/50 ml-auto">Digits absent from B1-B5 in last 5 draws</span>
                  </div>
                )}
                {/* All 7 Balls row */}
                {data.missing_digits_all && Object.keys(data.missing_digits_all).length > 0 && (
                  <div className="flex items-center gap-3 flex-wrap border-t border-white/10 pt-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-red-200/80 w-[140px] shrink-0">All 7 Balls</span>
                    <div className="flex items-center gap-2">
                      {Object.values(data.missing_digits_all).sort((a, b) => a.digit - b.digit).map(d => (
                        <span key={d.digit} className="inline-flex items-center gap-1 bg-red-500/30 backdrop-blur rounded-lg px-2.5 py-1 border border-red-300/40">
                          <span className="font-mono font-bold text-lg">{d.digit}</span>
                          <span className="text-[9px] text-white/70">{d.rebound_pct}%</span>
                        </span>
                      ))}
                    </div>
                    <span className="text-[10px] text-red-200/50 ml-auto">Strongest signal — absent from ALL balls + LS</span>
                  </div>
                )}
                {data.missing_digits_main && Object.keys(data.missing_digits_main).length > 0 && (!data.missing_digits_all || Object.keys(data.missing_digits_all).length === 0) && (
                  <div className="flex items-center gap-3 flex-wrap border-t border-white/10 pt-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-red-200/80 w-[140px] shrink-0">All 7 Balls</span>
                    <span className="text-[10px] text-white/40 italic">No digits missing from all 7 balls — all digits present via Lucky Stars</span>
                  </div>
                )}
              </div>
            )}

            {/* COMBINED PREDICTIONS (Date Digit + Class Mix) */}
            <TabsContent value="combined">
              {combinedLoading ? (
                <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-emerald-600" /></div>
              ) : combinedData ? (
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-emerald-800 via-teal-900 to-emerald-900 rounded-xl p-6 text-white shadow-xl">
                    <div className="flex items-center gap-3 mb-2">
                      <Crown size={32} weight="fill" className="text-yellow-400" />
                      <div>
                        <h2 className="text-2xl font-bold">Final Combined Predictions</h2>
                        <p className="text-sm text-white/60">
                          Cross-referenced from Date Digit Analysis + Class Mix Projections
                          {combinedData.next_draw_date && ` — Next draw: ${combinedData.next_draw_date}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs mb-4">
                      <span className="bg-white/10 px-2 py-1 rounded">
                        Date: {combinedData.date_window?.month_name} {combinedData.date_window?.day} ({combinedData.date_window?.total_matched} hist draws)
                      </span>
                      <span className="bg-white/10 px-2 py-1 rounded">
                        Missing DGs: {combinedData.date_window?.missing_dgs?.join(', ') || 'None'}
                      </span>
                      <span className="bg-white/10 px-2 py-1 rounded">
                        Pool: {combinedData.date_window?.pool_size} nums
                      </span>
                      <span className="bg-white/10 px-2 py-1 rounded">
                        Smart Picks: {combinedData.class_mix_window?.smart_picks_count}
                      </span>
                      {combinedData.sign_pattern_constraint && (
                        <span className="bg-amber-500/20 border border-amber-400/30 px-2 py-1 rounded text-amber-200">
                          W3 Sign: {combinedData.sign_pattern_constraint.hard_enforced?.length > 0
                            ? combinedData.sign_pattern_constraint.hard_enforced.join(', ')
                            : combinedData.sign_pattern_constraint.bonus_applied?.length > 0
                              ? `${combinedData.sign_pattern_constraint.bonus_applied.length} bonus positions`
                              : 'Dynamic'}
                        </span>
                      )}
                    </div>

                    {/* Dynamic Sign Pattern Constraint Banner */}
                    {combinedData.sign_pattern_constraint && (
                      <div className="mb-4 bg-amber-500/10 border border-amber-400/30 rounded-lg px-4 py-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-amber-400 font-bold text-xs">W3 DYNAMIC SIGN PATTERN</span>
                          <span className="text-xs text-amber-200">
                            Previous draw: [{combinedData.sign_pattern_constraint.prev_balls?.join(', ')}]
                          </span>
                        </div>

                        {/* Top overdue patterns */}
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {(combinedData.sign_pattern_constraint.top_overdue_patterns || []).map((p, i) => (
                            <span key={i} className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${p.is_overdue ? 'bg-red-500/20 text-red-300 border border-red-400/30' : 'bg-white/10 text-white/60'}`}>
                              {p.display.split(' ').map((s, si) => (
                                <span key={si} className={s === '+' ? 'text-emerald-400' : s === '-' ? 'text-red-400' : 'text-gray-400'}>
                                  {s === '+' ? '\u25B2' : s === '-' ? '\u25BC' : '='}
                                </span>
                              ))}
                              <span className="ml-1 text-white/40">{p.gap_ratio}x</span>
                            </span>
                          ))}
                        </div>

                        {/* Per-position analysis */}
                        <div className="flex flex-wrap gap-2 mb-1.5">
                          {Object.entries(combinedData.sign_pattern_constraint.position_analysis || {}).map(([pos, info]) => (
                            <div key={pos} className={`text-[10px] px-2 py-1 rounded flex items-center gap-1 ${
                              info.mode === 'hard' ? 'bg-red-500/20 border border-red-400/40 text-red-200' :
                              info.mode === 'bonus' ? 'bg-amber-500/20 border border-amber-400/40 text-amber-200' :
                              'bg-white/5 border border-white/10 text-white/40'
                            }`}>
                              <span className="font-bold">{pos}</span>
                              <span className="font-mono">
                                {info.dominant_sign === '-' ? '\u25BC' : info.dominant_sign === '+' ? '\u25B2' : '—'}
                              </span>
                              <span>{info.agreement_pct}%</span>
                              {info.mode === 'hard' && <span className="text-red-300 font-bold ml-0.5">HARD</span>}
                              {info.mode === 'bonus' && <span className="text-amber-300 font-bold ml-0.5">+15</span>}
                            </div>
                          ))}
                        </div>

                        {/* Summary rules */}
                        <div className="text-xs mt-1">
                          {combinedData.sign_pattern_constraint.hard_enforced?.length > 0 && (
                            <p className="text-red-300"><span className="font-bold">Hard:</span> {combinedData.sign_pattern_constraint.hard_enforced.join(' | ')}</p>
                          )}
                          {combinedData.sign_pattern_constraint.bonus_applied?.length > 0 && (
                            <p className="text-amber-300"><span className="font-bold">Bonus (+15 each):</span> {combinedData.sign_pattern_constraint.bonus_applied.join(' | ')}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Top Numbers */}
                    {combinedData.top_numbers && (
                      <div className="mb-4">
                        <h3 className="text-sm font-bold text-white/80 mb-2">Top Cross-Referenced Numbers</h3>
                        <div className="flex flex-wrap gap-2">
                          {combinedData.top_numbers.slice(0, 10).map((tn, i) => {
                            const g = consensus.groupOf(tn.number);
                            const gs = GROUP_STYLE[g];
                            return (
                            <div key={i} className="bg-white/10 rounded-lg px-2 py-1 flex items-center gap-1.5" title={`${g} · Consensus ${consensus.scoreOf(tn.number) >= 0 ? '+' : ''}${consensus.scoreOf(tn.number)}`}>
                              <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center bg-white text-gray-800 font-bold text-xs ring-2 ring-offset-1 ring-offset-transparent ${gs.ring}`}>{tn.number}</span>
                              <div>
                                <div className="text-[10px] font-bold text-yellow-300">{tn.score}pts</div>
                                <div className="flex gap-0.5">
                                  {tn.sources.slice(0, 3).map((s, j) => (
                                    <span key={j} className={`text-[7px] px-1 rounded ${s.startsWith('DATE') ? 'bg-cyan-500/30 text-cyan-200' : 'bg-rose-500/30 text-rose-200'}`}>{s}</span>
                                  ))}
                                </div>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Combined Combos — Consensus filter */}
                  <div className="bg-slate-900 text-white rounded-lg px-3 py-2 flex flex-wrap items-center gap-2 mb-3" data-testid="combined-consensus-filter">
                    <span className="text-[11px] font-bold">🚦 Consensus Filter:</span>
                    {[["OFF","Off"],["MAX_1_RED","Max 1 RED ⭐"],["NO_RED","No RED"],["ONLY_GREEN","Only GREEN"],["RERANK","Re-rank"]].map(([k,l]) => (
                      <button key={k} onClick={() => setConsensusFilter(k)} className={`text-[11px] px-2 py-0.5 rounded-full font-bold border ${consensusFilter === k ? 'bg-white text-slate-800 border-white' : 'bg-transparent text-white/80 border-white/20 hover:border-white/60'}`} data-testid={`combined-filter-${k}`}>{l}</button>
                    ))}
                    <span className="text-[10px] text-white/60 ml-2">Sorted: Premium → Solid → Caution</span>
                    <ConsensusRefLink className="ml-auto text-amber-300 hover:text-amber-200" />
                  </div>

                  <div className="space-y-3">
                    {(() => {
                      const list = applyConsensusFilter(combinedData.combined_combos || [], consensusFilter, consensus);
                      if (list.length === 0) {
                        return <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-center text-amber-700" data-testid="no-combined-filtered">No combos match the Consensus filter — try "Off" or "Max 1 RED".</div>;
                      }
                      return list.map((combo, ci) => {
                      const cmix = combo._quality.mix;
                      const cscore = combo._quality.score;
                      return (
                      <div key={ci} className={`bg-white border rounded-xl overflow-hidden ${ci === 0 ? 'ring-2 ring-emerald-500 border-emerald-500' : 'border-gray-200'}`} data-testid={`combined-combo-${ci}`}>
                        <div className={`px-4 py-3 flex items-center gap-4 flex-wrap ${ci === 0 ? 'bg-emerald-50' : 'bg-gray-50'}`}>
                          <div className="flex flex-col items-center">
                            <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center font-bold text-sm ${ci === 0 ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-700'}`}>#{ci + 1}</span>
                            <QualityBadge tier={combo._quality.tier} size="xs" />
                          </div>
                          <div className="flex items-center gap-2">
                            {combo.balls.map((n, j) => {
                              const g = consensus.groupOf(n);
                              const gs = GROUP_STYLE[g];
                              return (
                              <div key={j} className="flex flex-col items-center">
                                <span className="text-[8px] text-gray-400 font-bold">B{j + 1}</span>
                                <span className={`inline-flex w-10 h-10 rounded-full bg-[#002FA7] text-white items-center justify-center font-mono font-bold text-sm ring-2 ring-offset-1 ${gs.ring}`} title={`${g} · Consensus ${consensus.scoreOf(n) >= 0 ? '+' : ''}${consensus.scoreOf(n)}`}>{n}</span>
                                <div className="flex gap-0.5 mt-0.5">
                                  {combo.ball_sources[n]?.date?.length > 0 && <span className="w-2 h-2 rounded-full bg-cyan-400" title="Date signal" />}
                                  {combo.ball_sources[n]?.class_mix?.length > 0 && <span className="w-2 h-2 rounded-full bg-rose-400" title="Class Mix signal" />}
                                </div>
                                <span className="text-[8px] text-gray-400">{combo.ball_sources[n]?.combined_score}pts</span>
                              </div>
                              );
                            })}
                            <span className="text-gray-300 mx-1">+</span>
                            {combo.lucky_stars?.map((n, j) => (
                              <div key={j} className="flex flex-col items-center">
                                <span className="text-[8px] text-gray-400 font-bold">LS{j + 1}</span>
                                <span className="inline-flex w-10 h-10 rounded-full bg-amber-500 text-white items-center justify-center font-mono font-bold text-sm">{n}</span>
                              </div>
                            ))}
                          </div>
                          <div className="ml-auto flex items-center gap-4 flex-wrap">
                            <div className="flex items-center gap-1" data-testid={`combined-combo-${ci}-cmix`}>
                              {["GREEN","YELLOW","RED"].map(gk => (
                                <span key={gk} className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${GROUP_STYLE[gk].chip}`}>{gk[0]}:{cmix[gk]}</span>
                              ))}
                              <span className="text-[10px] font-mono font-bold text-slate-700 ml-1" title="Consensus sum">Σ {cscore >= 0 ? `+${cscore}` : cscore}</span>
                            </div>
                            <div className="text-right">
                              <div className="text-[10px] text-gray-500">Signal</div>
                              <div className="font-bold text-lg font-mono text-emerald-600">{combo.total_signal}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-[10px] text-gray-500">Structural</div>
                              <div className={`font-bold text-lg font-mono ${combo.verdict === 'EXCELLENT' ? 'text-emerald-600' : 'text-blue-600'}`}>{combo.structural_score}</div>
                              <div className={`text-[9px] font-bold px-1.5 rounded ${combo.verdict === 'EXCELLENT' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>{combo.verdict}</div>
                            </div>
                            {combo.missing_dgs_covered && combo.missing_dgs_covered.length > 0 && (
                              <div className="text-right">
                                <div className="text-[10px] text-violet-600 font-bold">DG {combo.missing_dgs_covered.join(',')} filled</div>
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Source breakdown */}
                        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex flex-wrap gap-2 text-[9px]">
                          {combo.balls.map((n, j) => {
                            const src = combo.ball_sources[n] || {};
                            const allSrcs = [...(src.date || []), ...(src.class_mix || [])];
                            return allSrcs.length > 0 ? (
                              <div key={j} className="flex items-center gap-1">
                                <span className="font-bold text-gray-600">#{n}:</span>
                                {allSrcs.slice(0, 3).map((s, k) => (
                                  <span key={k} className={`px-1 rounded ${s.startsWith('DATE') ? 'bg-cyan-100 text-cyan-700' : 'bg-rose-100 text-rose-700'}`}>{s}</span>
                                ))}
                              </div>
                            ) : null;
                          })}
                          <div className="flex items-center gap-1 ml-auto">
                            <span className="w-2 h-2 rounded-full bg-cyan-400" /> <span className="text-gray-400">Date Digit</span>
                            <span className="w-2 h-2 rounded-full bg-rose-400 ml-1" /> <span className="text-gray-400">Class Mix</span>
                          </div>
                        </div>
                      </div>
                      );
                      });
                    })()}
                  </div>
                </div>
              ) : (
                <div className="text-center py-10 text-gray-500">Failed to load combined predictions</div>
              )}

              {/* MARKOV CHAIN LS PREDICTION */}
              {data?.ls_diagnostics?.markov_pairs && (
                <div className="bg-gradient-to-br from-violet-900 via-purple-900 to-indigo-900 rounded-xl p-6 text-white shadow-xl mt-6" data-testid="markov-ls-section">
                  <div className="flex items-center gap-3 mb-4">
                    <Lightning size={28} weight="fill" className="text-yellow-400" />
                    <div>
                      <h2 className="text-xl font-bold">Markov Chain — LS Pair Prediction</h2>
                      <p className="text-xs text-white/50">
                        Based on W40 transition probabilities from last drawn values — LS1: <b className="text-yellow-300">#{data.ls_diagnostics.ls_last_values?.lucky_star_1}</b>, LS2: <b className="text-yellow-300">#{data.ls_diagnostics.ls_last_values?.lucky_star_2}</b>
                      </p>
                    </div>
                  </div>

                  {/* Top 3 pairs as hero cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {data.ls_diagnostics.markov_pairs.slice(0, 3).map((p, idx) => {
                      const statusColor = (s) => ({
                        'HOT': 'text-red-300', 'COLD': 'text-blue-300', 'HEATING': 'text-orange-300',
                        'COOLING': 'text-cyan-300', 'NEUTRAL': 'text-gray-300'
                      }[s] || 'text-gray-300');
                      return (
                        <div key={idx} className={`rounded-lg p-4 ${idx === 0 ? 'bg-yellow-500/20 border border-yellow-400/40 ring-1 ring-yellow-400/30' : 'bg-white/5 border border-white/10'}`} data-testid={`markov-pair-${idx}`}>
                          <div className="flex items-center justify-between mb-3">
                            <span className={`text-xs font-bold uppercase ${idx === 0 ? 'text-yellow-300' : 'text-white/40'}`}>
                              {idx === 0 ? 'Most Probable' : `#${idx + 1}`}
                            </span>
                            <span className={`text-2xl font-mono font-black ${idx === 0 ? 'text-yellow-300' : 'text-white'}`}>{p.combined_prob}%</span>
                          </div>
                          <div className="flex items-center justify-center gap-3 mb-3">
                            <div className="flex flex-col items-center">
                              <span className="text-[9px] text-white/40 uppercase">LS1</span>
                              <span className={`inline-flex w-12 h-12 rounded-full items-center justify-center font-mono font-black text-xl ${idx === 0 ? 'bg-yellow-400 text-yellow-900' : 'bg-yellow-200 text-yellow-800'}`}>{p.ls1}</span>
                              <span className="text-[10px] font-mono text-white/60 mt-1">{p.ls1_prob}%</span>
                              <span className={`text-[9px] font-bold ${statusColor(p.ls1_status)}`}>{p.ls1_status}</span>
                            </div>
                            <span className="text-white/30 text-lg">+</span>
                            <div className="flex flex-col items-center">
                              <span className="text-[9px] text-white/40 uppercase">LS2</span>
                              <span className={`inline-flex w-12 h-12 rounded-full items-center justify-center font-mono font-black text-xl ${idx === 0 ? 'bg-yellow-400 text-yellow-900' : 'bg-yellow-200 text-yellow-800'}`}>{p.ls2}</span>
                              <span className="text-[10px] font-mono text-white/60 mt-1">{p.ls2_prob}%</span>
                              <span className={`text-[9px] font-bold ${statusColor(p.ls2_status)}`}>{p.ls2_status}</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-center gap-3 text-[10px] text-white/50">
                            <span>Sum: {p.pair_sum} {p.sum_in_top ? <span className="text-green-400">&#10003;</span> : ''}</span>
                            <span>|</span>
                            <span>Spread: {p.pair_spread} {p.spread_in_top ? <span className="text-green-400">&#10003;</span> : ''}</span>
                            <span>|</span>
                            <span>Hist: {p.hist_pair_count}x</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Remaining pairs as compact table */}
                  <div className="bg-black/20 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-[9px] uppercase text-white/40 border-b border-white/10">
                          <th className="px-3 py-2 text-center">#</th>
                          <th className="px-3 py-2 text-center">LS1</th>
                          <th className="px-3 py-2 text-center">LS2</th>
                          <th className="px-3 py-2 text-center">P(LS1)</th>
                          <th className="px-3 py-2 text-center">P(LS2)</th>
                          <th className="px-3 py-2 text-center">Joint %</th>
                          <th className="px-3 py-2 text-center">Sum</th>
                          <th className="px-3 py-2 text-center">Spread</th>
                          <th className="px-3 py-2 text-center">Hist</th>
                          <th className="px-3 py-2 text-center">LS1 Gap</th>
                          <th className="px-3 py-2 text-center">LS2 Gap</th>
                          <th className="px-3 py-2 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.ls_diagnostics.markov_pairs.slice(3, 15).map((p, idx) => {
                          const statusBadge = (s) => {
                            const cls = { 'HOT': 'bg-red-500/30 text-red-300', 'COLD': 'bg-blue-500/30 text-blue-300', 'HEATING': 'bg-orange-500/30 text-orange-300', 'COOLING': 'bg-cyan-500/30 text-cyan-300', 'NEUTRAL': 'bg-gray-500/30 text-gray-300' }[s] || 'bg-gray-500/30';
                            return <span className={`px-1 py-0.5 rounded text-[8px] font-bold ${cls}`}>{s?.substring(0, 4)}</span>;
                          };
                          return (
                            <tr key={idx} className="border-b border-white/5 hover:bg-white/5" data-testid={`markov-row-${idx + 4}`}>
                              <td className="px-3 py-2 text-center text-white/40 font-mono">{idx + 4}</td>
                              <td className="px-3 py-2 text-center"><span className="inline-flex w-7 h-7 rounded-full bg-yellow-200 text-yellow-800 items-center justify-center font-mono font-bold">{p.ls1}</span></td>
                              <td className="px-3 py-2 text-center"><span className="inline-flex w-7 h-7 rounded-full bg-yellow-200 text-yellow-800 items-center justify-center font-mono font-bold">{p.ls2}</span></td>
                              <td className="px-3 py-2 text-center font-mono text-white/70">{p.ls1_prob}%</td>
                              <td className="px-3 py-2 text-center font-mono text-white/70">{p.ls2_prob}%</td>
                              <td className="px-3 py-2 text-center font-mono font-bold text-yellow-300">{p.combined_prob}%</td>
                              <td className={`px-3 py-2 text-center font-mono ${p.sum_in_top ? 'text-green-400 font-bold' : 'text-white/40'}`}>{p.pair_sum}</td>
                              <td className={`px-3 py-2 text-center font-mono ${p.spread_in_top ? 'text-green-400 font-bold' : 'text-white/40'}`}>{p.pair_spread}</td>
                              <td className="px-3 py-2 text-center font-mono text-white/50">{p.hist_pair_count}x</td>
                              <td className={`px-3 py-2 text-center font-mono ${p.ls1_gap_ratio >= 2 ? 'text-red-400 font-bold' : p.ls1_gap_ratio >= 1 ? 'text-amber-400' : 'text-white/40'}`}>{p.ls1_gap_ratio}x</td>
                              <td className={`px-3 py-2 text-center font-mono ${p.ls2_gap_ratio >= 2 ? 'text-red-400 font-bold' : p.ls2_gap_ratio >= 1 ? 'text-amber-400' : 'text-white/40'}`}>{p.ls2_gap_ratio}x</td>
                              <td className="px-3 py-2 text-center">{statusBadge(p.ls1_status)} {statusBadge(p.ls2_status)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* MULTI-WINDOW PROJECTION */}
            <TabsContent value="multi-window">
              <MultiWindowProjection />
            </TabsContent>

            {/* TOP VALIDATED COMBINATIONS */}
            <TabsContent value="predictions">
              <div className="bg-gradient-to-br from-indigo-800 via-blue-900 to-indigo-900 rounded-xl p-6 mb-6 text-white shadow-xl">
                <div className="flex items-center gap-3 mb-4">
                  <Crown size={32} weight="fill" className="text-yellow-400" />
                  <div>
                    <h2 className="text-2xl font-bold">Top 10 Validated Combinations</h2>
                    <p className="text-sm text-white/60">5 Balls + 2 Lucky Stars — aggregated from {(data.sources || []).length} engines, filtered through Combination Validator</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-4 text-xs">
                  {(data.sources || []).map(s => (
                    <span key={s} className="bg-white/10 px-2 py-1 rounded font-mono">{s}</span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3 mb-6 text-xs">
                  <span className="bg-white/10 px-2 py-1 rounded">Sum: {data.validation_rules?.sum_range?.min}-{data.validation_rules?.sum_range?.max}</span>
                  <span className="bg-white/10 px-2 py-1 rounded">Spread: {data.validation_rules?.spread_range?.min}-{data.validation_rules?.spread_range?.max}</span>
                  <span className="bg-white/10 px-2 py-1 rounded">Odd/Even: 1-4</span>
                  <span className="bg-white/10 px-2 py-1 rounded">Max per range: 3</span>
                  {data.validation_rules?.ls_sum_range && (
                    <span className="bg-yellow-400/20 px-2 py-1 rounded text-yellow-300">LS Sum: {data.validation_rules.ls_sum_range.min}-{data.validation_rules.ls_sum_range.max}</span>
                  )}
                  {data.w20_constraints && (
                    <span className="bg-teal-400/20 px-2 py-1 rounded text-teal-300">W20 Peak: {data.w20_constraints.peak_spread_min}-{data.w20_constraints.peak_spread_max} ({data.w20_constraints.peak_spread_pct}%)</span>
                  )}
                  {data.w20_constraints && (
                    <span className="bg-cyan-400/15 px-2 py-1 rounded text-cyan-300">W20 Ext: {data.w20_constraints.extended_spread_min}-{data.w20_constraints.extended_spread_max} ({data.w20_constraints.extended_spread_pct}%)</span>
                  )}
                </div>

                {/* W20 Spread Prediction - Top 3 Most Likely */}
                {data.w20_constraints?.bucket_stats && (() => {
                  const sorted = [...data.w20_constraints.bucket_stats].sort((a, b) => b.pred_score - a.pred_score);
                  const top3 = sorted.slice(0, 3);
                  return (
                    <div className="bg-gradient-to-r from-teal-900/50 to-cyan-900/40 border border-teal-500/30 rounded-lg p-4 mb-6" data-testid="w20-spread-prediction">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-teal-300 text-sm">W20 Spread Prediction — Next Draw</h3>
                          <p className="text-[10px] text-white/50">Top 3 most-likely spread buckets by frequency + overdue gap ratio</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        {top3.map((b, idx) => {
                          const medal = idx === 0 ? 'text-yellow-400' : idx === 1 ? 'text-gray-300' : 'text-amber-600';
                          return (
                            <div key={b.bucket} className={`rounded-lg p-3 ${idx === 0 ? 'bg-teal-500/20 ring-1 ring-teal-400/50' : 'bg-white/5'}`} data-testid={`w20-pred-${idx}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className={`font-bold text-xs ${medal}`}>#{idx + 1}</span>
                                <span className={`font-mono font-bold text-lg ${idx === 0 ? 'text-teal-300' : 'text-white/80'}`}>{b.label}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-white/60 text-[10px]">Freq: <span className="text-white font-bold">{b.pct}%</span></div>
                                  <div className="text-white/60 text-[10px]">Gap: <span className={`font-bold ${b.current_gap > b.avg_gap ? 'text-red-400' : 'text-white'}`}>{b.current_gap}</span></div>
                                  <div className="text-white/60 text-[10px]">Avg: <span className="text-white">{b.avg_gap}</span> | Max: <span className="text-white">{b.max_gap}</span></div>
                                </div>
                                <div className="text-right">
                                  <div className={`font-mono font-bold text-xl ${b.gap_ratio >= 1.5 ? 'text-red-400' : b.gap_ratio >= 1 ? 'text-amber-400' : 'text-teal-300'}`}>{b.pred_score}</div>
                                  <div className={`text-[9px] font-bold ${b.gap_ratio >= 1.5 ? 'text-red-400' : b.gap_ratio >= 1 ? 'text-amber-400' : 'text-white/40'}`}>{b.gap_ratio}x due</div>
                                  {b.is_peak && <div className="text-[8px] text-teal-400 font-bold">PEAK</div>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                <div className="space-y-3" data-testid="validated-combos">
                  {/* Consensus filter control */}
                  <div className="bg-slate-900/40 border border-white/10 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2 mb-2" data-testid="consensus-filter-bar">
                    <span className="text-[11px] font-bold text-white/80">🚦 Consensus Filter:</span>
                    {[["OFF","Off"],["MAX_1_RED","Max 1 RED ⭐"],["NO_RED","No RED"],["ONLY_GREEN","Only GREEN"],["RERANK","Re-rank"]].map(([k,l]) => (
                      <button key={k} onClick={() => setConsensusFilter(k)} className={`text-[11px] px-2 py-0.5 rounded-full font-bold border ${consensusFilter === k ? 'bg-white text-slate-800 border-white' : 'bg-transparent text-white/80 border-white/20 hover:border-white/60'}`} data-testid={`consensus-filter-${k}`}>{l}</button>
                    ))}
                    <span className="text-[10px] text-white/50 ml-2">Default: Max 1 RED · Always sorted Premium → Solid → Caution</span>
                    <ConsensusRefLink className="ml-auto text-amber-300 hover:text-amber-200" />
                  </div>
                  {(() => {
                    const list = applyConsensusFilter(data.validated_combinations || [], consensusFilter, consensus);
                    if (list.length === 0) {
                      return <div className="bg-amber-500/20 border border-amber-500/40 text-amber-200 rounded p-3 text-xs text-center" data-testid="no-combos-filtered">No validated combos match the Consensus filter — try "Off" or "Max 1 RED".</div>;
                    }
                    return list.map((c, idx) => {
                    const cmix = c._quality.mix;
                    const cscore = c._quality.score;
                    return (
                    <div key={idx} className={`flex items-center gap-4 p-4 rounded-lg ${idx === 0 ? 'bg-gradient-to-r from-yellow-400/20 to-amber-400/10 ring-1 ring-yellow-400/30' : 'bg-white/5'}`} data-testid={`combo-${idx}`}>
                      <div className="flex flex-col items-center shrink-0">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${idx === 0 ? 'bg-yellow-400 text-yellow-900' : idx < 3 ? 'bg-white/20' : 'bg-white/10'}`}>
                          {idx + 1}
                        </span>
                        <QualityBadge tier={c._quality.tier} size="xs" />
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {/* 5 Main Balls */}
                        {c.balls.map((b, i) => {
                          const g = consensus.groupOf(b);
                          const gs = GROUP_STYLE[g];
                          return (
                          <span key={i} className={`inline-flex w-11 h-11 rounded-full items-center justify-center font-mono font-bold text-lg ring-2 ring-offset-1 ring-offset-transparent ${gs.ring} ${idx === 0 ? 'bg-white text-[#002FA7] shadow-lg' : 'bg-white/15 text-white border border-white/20'}`} data-testid={`combo-${idx}-ball-${i}`} title={`${g} · Consensus ${consensus.scoreOf(b) >= 0 ? '+' : ''}${consensus.scoreOf(b)}`}>{b}</span>
                          );
                        })}
                        {/* Separator */}
                        <span className="text-white/30 text-xl mx-1">+</span>
                        {/* 2 Lucky Stars */}
                        {(c.lucky_stars || []).map((ls, i) => (
                          <span key={`ls-${i}`} className={`inline-flex w-11 h-11 rounded-full items-center justify-center font-mono font-bold text-lg ${idx === 0 ? 'bg-yellow-400 text-yellow-900 shadow-lg' : 'bg-yellow-400/25 text-yellow-300 border border-yellow-400/40'}`} data-testid={`combo-${idx}-star-${i}`}>
                            <Star size={10} weight="fill" className="absolute -top-0.5 -right-0.5 opacity-60" style={{position:'relative', top:'-1px', marginRight:'2px'}} />{ls}
                          </span>
                        ))}
                      </div>
                      <div className="flex-1 flex items-center gap-4 text-xs flex-wrap">
                        <span className="font-mono">Sum: <b>{c.sum}</b></span>
                        <span className="font-mono">Spread: <b>{c.spread}</b></span>
                        <span className={`font-mono px-1.5 py-0.5 rounded text-[10px] font-bold ${c.b5b1_zone === 'sweet' ? 'bg-emerald-100 text-emerald-800' : c.b5b1_zone === 'standard' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>B5-B1: {c.spread} ({c.b5b1_pct}%)</span>
                        <span className={`font-mono px-1.5 py-0.5 rounded text-[10px] font-bold ${c.odd_even_pct >= 90 ? 'bg-emerald-100 text-emerald-800' : c.odd_even_pct >= 43 ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>{c.odds}O/{5 - c.odds}E ({c.odd_even_pct}%)</span>
                        <span className={`font-mono px-1.5 py-0.5 rounded text-[10px] font-bold ${(c.high_low_pct || 0) >= 90 ? 'bg-emerald-100 text-emerald-800' : (c.high_low_pct || 0) >= 43 ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>{c.high || 0}H/{5 - (c.high || 0)}L ({c.high_low_pct || 0}%)</span>
                        <span className="font-mono opacity-60">[{c.range}]</span>
                        {c.w20_spread_tier === 'peak' && <span className="bg-teal-100 text-teal-800 px-1.5 py-0.5 rounded text-[10px] font-bold">W20 Peak</span>}
                        {c.w20_spread_tier === 'extended' && <span className="bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded text-[10px] font-bold">W20 Ext</span>}
                        {c.w20_quartile_match && <span className="bg-cyan-100 text-cyan-800 px-1.5 py-0.5 rounded text-[10px] font-bold">W20 Quartile</span>}
                        {c.w20_overdue_bonus > 0 && <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-[10px] font-bold">Overdue {c.w20_spread_bucket} +{c.w20_overdue_bonus}</span>}
                        {c.w20_bonus > 0 && <span className="text-teal-300 font-mono font-bold text-[10px]">+{c.w20_bonus}</span>}
                        {c.ls_spread_bonus > 0 && <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded text-[10px] font-bold">LS d={c.ls_spread} {c.ls_spread_due}x +{c.ls_spread_bonus}</span>}
                        {(c.missing_main_count || 0) > 0 && <span className="bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded text-[10px] font-bold">Main: {c.missing_main_count}/5</span>}
                        {(c.missing_all_count || 0) > 0 && <span className="bg-red-100 text-red-800 px-1.5 py-0.5 rounded text-[10px] font-bold">All7: {c.missing_all_count}</span>}
                        {/* Consensus summary chip */}
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-200 text-slate-800" data-testid={`combo-${idx}-cmix`}>
                          🚦 G{cmix.GREEN} Y{cmix.YELLOW} R{cmix.RED} <span className="text-slate-600">(Σ {cscore >= 0 ? `+${cscore}` : cscore})</span>
                        </span>
                      </div>
                      <div className="text-[10px] text-white/50 flex gap-3 mt-0.5">
                        <span>LS Sum: {c.ls_sum}</span>
                        <span>LS Spread: {c.ls_spread} (avg {c.ls_spread_avg_gap} / max {c.ls_spread_max_gap})</span>
                      </div>
                      <div className={`text-right shrink-0 ${idx === 0 ? 'text-yellow-400' : ''}`}>
                        <div className="font-mono font-bold text-lg">{c.consensus_score}</div>
                        <div className="text-[10px] opacity-60">score</div>
                        <div className="text-[10px] font-mono font-bold text-emerald-300 mt-0.5" title="Consensus sum (G=+1 to +7, R=negative)">C: {cscore >= 0 ? `+${cscore}` : cscore}</div>
                      </div>
                    </div>
                    );
                    });
                  })()}
                </div>
              </div>
            </TabsContent>

            {/* PER-WINDOW BALL PREDICTIONS */}
            <TabsContent value="per-window">
              <SummaryTabPerWindow
                pwData={pwData}
                pwLoading={pwLoading}
                w3Projection={w3Projection}
                dateSignal={dateSignal}
                exportPwToExcel={exportPwToExcel}
                consensus={consensus}
                ballLabels={ballLabels}
                lsLabels={lsLabels}
              />
            </TabsContent>

            {/* PER-BALL CONSENSUS SCORES */}
            <TabsContent value="consensus">
              {/* Backtest Accuracy Panel */}
              {data.adaptive_weights && (
                <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-5 mb-6 text-white shadow-lg" data-testid="backtest-accuracy-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center"><ChartBar size={18} className="text-amber-400" /></div>
                    <div>
                      <h3 className="text-sm font-bold">Adaptive W11 × W21 Weighting — Backtested on last {data.backtest_results?.ball_1?.tests || 30} draws</h3>
                      <p className="text-[10px] text-white/50">Weights are dynamically adjusted per position based on which window was historically more accurate</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-5 gap-3" data-testid="backtest-grid">
                    {Object.entries(ballLabels).map(([key, label]) => {
                      const aw = data.adaptive_weights?.[key] || {};
                      const trustColor = aw.trust === 'W11' ? 'text-blue-400' : aw.trust === 'W21' ? 'text-emerald-400' : 'text-amber-400';
                      const w11Bar = Math.min(aw.w11_acc_pct || 0, 100);
                      const w21Bar = Math.min(aw.w21_acc_pct || 0, 100);
                      return (
                        <div key={key} className="bg-white/5 rounded-lg p-3 border border-white/10" data-testid={`backtest-${key}`}>
                          <div className="text-center mb-2">
                            <span className="text-xs font-bold text-white/80">{label}</span>
                            <div className={`text-[10px] font-bold ${trustColor}`}>Trust: {aw.trust || 'N/A'}</div>
                          </div>
                          <div className="space-y-2">
                            <div>
                              <div className="flex items-center justify-between text-[9px] mb-0.5">
                                <span className="text-blue-300 font-bold">W11</span>
                                <span className="text-white/60">{aw.w11_top5_hits || 0}/{aw.tests || 0} top5 ({aw.w11_acc_pct || 0}%)</span>
                              </div>
                              <div className="h-1.5 bg-white/10 rounded-full">
                                <div className="h-full bg-blue-400 rounded-full" style={{width: `${w11Bar}%`}} />
                              </div>
                            </div>
                            <div>
                              <div className="flex items-center justify-between text-[9px] mb-0.5">
                                <span className="text-emerald-300 font-bold">W21</span>
                                <span className="text-white/60">{aw.w21_top5_hits || 0}/{aw.tests || 0} top5 ({aw.w21_acc_pct || 0}%)</span>
                              </div>
                              <div className="h-1.5 bg-white/10 rounded-full">
                                <div className="h-full bg-emerald-400 rounded-full" style={{width: `${w21Bar}%`}} />
                              </div>
                            </div>
                            <div className="flex justify-between text-[8px] text-white/40 pt-1 border-t border-white/10">
                              <span>W11 wt: {(aw.w11_weight || 0).toFixed(2)}</span>
                              <span>W21 wt: {(aw.w21_weight || 0).toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Main Balls */}
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Main Balls (1-50) — W11 x W21 Convergence Scoring</h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
                {Object.entries(ballLabels).map(([key, label]) => {
                  const nums = data.consensus?.[key] || [];
                  return (
                    <div key={key} className="bg-white border rounded p-4">
                      <h3 className="font-bold text-gray-900 mb-3 text-center">{label}</h3>
                      <div className="space-y-1" data-testid={`consensus-${key}`}>
                        {nums.map((n, idx) => {
                          const convColor = n.convergence === 'CONVERGE' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : n.convergence === 'CONFLICT' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-gray-50 text-gray-500 border-gray-200';
                          const convIcon = n.convergence === 'CONVERGE' ? '++' : n.convergence === 'CONFLICT' ? '!!' : '--';
                          return (
                          <div key={n.number} className={`flex items-center gap-1.5 px-2 py-1 rounded ${idx === 0 ? 'bg-yellow-100 ring-1 ring-yellow-300' : idx < 3 ? 'bg-blue-50' : 'bg-gray-50'}`}>
                            <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-bold text-xs shrink-0 ${idx === 0 ? 'bg-yellow-400 text-yellow-900' : 'bg-[#002FA7]/10 text-[#002FA7]'}`}>{n.number}</span>
                            <div className="flex-1 min-w-0">
                              <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                                <div className={`h-full rounded-full ${idx === 0 ? 'bg-yellow-500' : 'bg-[#002FA7]'}`} style={{ width: `${n.normalized}%` }} />
                              </div>
                              <div className="flex items-center justify-between mt-0.5">
                                <div className="flex items-center gap-1">
                                  <span className="text-[8px] text-gray-400">W11:{n.w11_pct || 0}%</span>
                                  <span className="text-[8px] text-gray-400">W21:{n.w21_pct || 0}%</span>
                                  <span className={`text-[8px] font-bold ${(n.pos_pct || 0) >= 5 ? 'text-emerald-600' : (n.pos_pct || 0) >= 2 ? 'text-blue-500' : 'text-red-400'}`}>P:{n.pos_pct || 0}%</span>
                                </div>
                                <span className={`text-[8px] font-bold px-1 rounded border ${convColor}`}>{convIcon}</span>
                              </div>
                            </div>
                            <span className="font-mono text-[10px] font-bold w-10 text-right shrink-0">{n.score}</span>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Lucky Stars — Enhanced with W39-42 signals */}
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Star size={16} weight="fill" className="text-yellow-500" /> Lucky Stars (1-12) — W39-42 Enhanced
                {data.ls_diagnostics && (
                  <span className="text-[10px] text-gray-400 font-normal ml-2">
                    Last LS1: <b>{data.ls_diagnostics.ls_last_values?.lucky_star_1}</b> |
                    Last LS2: <b>{data.ls_diagnostics.ls_last_values?.lucky_star_2}</b>
                  </span>
                )}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
                {Object.entries(lsLabels).map(([key, label]) => {
                  const nums = data.ls_consensus?.[key] || [];
                  return (
                    <div key={key} className="bg-white border rounded-lg p-4 border-yellow-300" data-testid={`consensus-${key}-card`}>
                      <h3 className="font-bold text-gray-900 mb-3 text-center flex items-center justify-center gap-1">
                        <Star size={14} weight="fill" className="text-yellow-500" /> {label}
                      </h3>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[9px] uppercase text-gray-500">
                            <th className="px-1 py-1 text-center">#</th>
                            <th className="px-1 py-1 text-left">Score</th>
                            <th className="px-1 py-1 text-center">Gap</th>
                            <th className="px-1 py-1 text-center">Markov</th>
                            <th className="px-1 py-1 text-center">Status</th>
                            <th className="px-1 py-1 text-center">Trend</th>
                          </tr>
                        </thead>
                        <tbody data-testid={`consensus-${key}`}>
                          {nums.map((n, idx) => {
                            const statusColor = {
                              'HOT': 'bg-red-100 text-red-700',
                              'COLD': 'bg-blue-100 text-blue-700',
                              'HEATING': 'bg-orange-100 text-orange-700',
                              'COOLING': 'bg-cyan-100 text-cyan-700',
                              'NEUTRAL': 'bg-gray-100 text-gray-600'
                            }[n.hc_status] || 'bg-gray-100 text-gray-600';
                            const trendIcon = n.hc_trend === 'UP' ? '↑' : n.hc_trend === 'DOWN' ? '↓' : '→';
                            const trendColor = n.hc_trend === 'UP' ? 'text-green-600' : n.hc_trend === 'DOWN' ? 'text-red-600' : 'text-gray-400';
                            return (
                              <tr key={n.number} className={idx === 0 ? 'bg-yellow-100 ring-1 ring-yellow-300' : idx < 3 ? 'bg-yellow-50' : ''}>
                                <td className="px-1 py-1.5 text-center">
                                  <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-bold text-xs ${idx === 0 ? 'bg-yellow-400 text-yellow-900' : 'bg-yellow-200 text-yellow-800'}`}>{n.number}</span>
                                </td>
                                <td className="px-1 py-1.5">
                                  <div className="flex items-center gap-1">
                                    <div className="bg-gray-200 rounded-full h-2 overflow-hidden flex-1">
                                      <div className={`h-full rounded-full ${idx === 0 ? 'bg-yellow-500' : 'bg-yellow-400'}`} style={{ width: `${n.normalized}%` }} />
                                    </div>
                                    <span className="font-mono text-[10px] font-bold w-10 text-right">{n.score}</span>
                                  </div>
                                </td>
                                <td className={`px-1 py-1.5 text-center font-mono font-bold text-[10px] ${(n.gap_ratio || 0) >= 2 ? 'text-red-600' : (n.gap_ratio || 0) >= 1 ? 'text-amber-600' : 'text-gray-400'}`}>
                                  {(n.gap_ratio || 0) > 0 ? `${n.gap_ratio}x` : '-'}
                                </td>
                                <td className={`px-1 py-1.5 text-center font-mono font-bold text-[10px] ${(n.markov_pct || 0) >= 15 ? 'text-green-600' : (n.markov_pct || 0) >= 10 ? 'text-blue-600' : 'text-gray-400'}`}>
                                  {(n.markov_pct || 0) > 0 ? `${n.markov_pct}%` : '-'}
                                </td>
                                <td className="px-1 py-1.5 text-center">
                                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${statusColor}`}>{n.hc_status || '-'}</span>
                                </td>
                                <td className={`px-1 py-1.5 text-center font-bold text-sm ${trendColor}`}>
                                  {trendIcon}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>

              {/* W42 Sum/Spread Diagnostic */}
              {data.ls_diagnostics && (
                <div className="mt-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4 max-w-4xl" data-testid="ls-w42-diagnostic">
                  <div className="flex items-center gap-2 mb-2">
                    <Star size={14} weight="fill" className="text-purple-500" />
                    <span className="text-xs font-bold text-purple-700 uppercase">W42 LS Sum & Spread — Top Probability Ranges</span>
                  </div>
                  <div className="flex gap-6 text-xs">
                    <div>
                      <span className="text-gray-500 font-bold">Top Sums (LS1+LS2): </span>
                      {data.ls_diagnostics.top_sums?.map(s => (
                        <span key={s} className="inline-flex w-6 h-6 rounded-full bg-purple-200 text-purple-800 items-center justify-center font-mono font-bold text-[10px] mx-0.5">{s}</span>
                      ))}
                    </div>
                    <div>
                      <span className="text-gray-500 font-bold">Top Spreads (LS2-LS1): </span>
                      {data.ls_diagnostics.top_spreads?.map(s => (
                        <span key={s} className="inline-flex w-6 h-6 rounded-full bg-indigo-200 text-indigo-800 items-center justify-center font-mono font-bold text-[10px] mx-0.5">{s}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* WINDOW IMPORTANCE RANKING — ALL 37 */}
            <TabsContent value="ranking">
              <div className="bg-white border rounded overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
                  <span className="text-sm font-bold text-gray-700">All 37 Windows Ranked by Predictive Importance</span>
                  <span className="text-xs text-gray-500">{IMPORTANCE_DATA.length} windows</span>
                </div>
                <div className="overflow-x-auto" data-testid="importance-table">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-center w-12">Rank</th>
                        <th className="px-4 py-3 text-left">Window</th>
                        <th className="px-4 py-3 text-center w-28">Importance</th>
                        <th className="px-4 py-3 text-left">Rationale</th>
                        <th className="px-4 py-3 text-center w-16">Go</th>
                      </tr>
                    </thead>
                    <tbody>
                      {IMPORTANCE_DATA.map((item, idx) => (
                        <tr key={item.rank} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`rank-row-${item.rank}`}>
                          <td className="px-4 py-3 border-b text-center">
                            <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-bold text-xs ${item.rank <= 3 ? 'bg-[#002FA7] text-white' : item.rank <= 10 ? 'bg-amber-100 text-amber-800' : 'bg-gray-200 text-gray-600'}`}>{item.rank}</span>
                          </td>
                          <td className="px-4 py-3 border-b font-bold">
                            <span className="text-gray-400 text-xs mr-1">W{item.window}:</span>
                            {item.name}
                          </td>
                          <td className="px-4 py-3 border-b text-center"><TierBadge tier={item.tier} /></td>
                          <td className="px-4 py-3 border-b text-xs text-gray-600">{item.rationale}</td>
                          <td className="px-4 py-3 border-b text-center">
                            <Button variant="ghost" size="sm" onClick={() => navigate(item.path)} className="h-7 w-7 p-0" data-testid={`go-w${item.window}`}>
                              <ArrowRight size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        ) : null}
      </main>
    </div>
  );
}
